import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { roundGrams, splitItemsText } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type { LocalFood, LocalRecipe, LocalRecipeIngredient } from '../../db/local.js';
import { addCookNote, stepsOf, today } from '../../db/nutrition.js';
import { readProfile } from '../../db/profile.js';
import { useWakeLock } from '../../hooks/useWakeLock.js';

/**
 * Kochen: erst bereitlegen, dann ein Schritt je Bildschirm, am Ende eine Notiz.
 *
 * Große Flächen, große Schrift — mit nassen Händen trifft man nichts Kleines.
 * Der Bildschirm bleibt an (Wake Lock), solange der Kochmodus offen ist; geht
 * das nicht, steht es da, statt dass das Telefon mitten im Schritt schwarz wird
 * und keiner weiß, warum.
 */
export function CookMode({
  recipe,
  ingredients,
  foods,
  factor,
  portions,
  onClose,
}: {
  recipe: LocalRecipe;
  ingredients: LocalRecipeIngredient[];
  foods: Map<string, LocalFood>;
  factor: number;
  portions: number;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const steps = stepsOf(recipe);
  // Ohne Zutatenliste die alte Zutatenzeile („80 g Hafer · Beeren") —
  // Gerichte aus dem Startbestand haben nur sie. Sie wird nicht umgerechnet:
  // Mengen im Freitext kann niemand verlässlich lesen.
  const lines: { id: string; label: string }[] =
    ingredients.length > 0
      ? ingredients.map((entry) => {
          const food = entry.foodId ? foods.get(entry.foodId) : undefined;
          return {
            id: entry.id,
            label:
              entry.foodId === null
                ? (entry.label ?? '')
                : `${roundGrams((entry.grams ?? 0) * factor)} g ${
                    food?.name ?? t('nutrition.recipe.unknownFood')
                  }`,
          };
        })
      : splitItemsText(recipe.items).map((label, index) => ({ id: `item-${index}`, label }));
  // -1 = bereitlegen, 0 … n-1 = Schritte, n = fertig.
  const [phase, setPhase] = useState(lines.length > 0 ? -1 : 0);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const awake = useWakeLock();

  const atEnd = phase >= steps.length;

  return (
    <section aria-labelledby="cook-heading" className="flex min-h-dvh flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h2 id="cook-heading" className="font-display text-xl font-bold break-words">
          {recipe.name}
        </h2>
        <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet kw-btn-small px-3">
          {t('cook.close')}
        </button>
      </div>
      {awake === 'unsupported' ? (
        <p role="status" className="text-xs text-warn">
          {t('cook.noWakeLock')}
        </p>
      ) : null}

      {phase === -1 ? (
        <div className="flex flex-col gap-3">
          <p className="kw-kicker">
            {ingredients.length > 0
              ? t('cook.prepare', { count: portions })
              : t('cook.prepareAsWritten')}
          </p>
          <ul className="flex flex-col gap-2">
            {lines.map((line) => {
              const done = checked.has(line.id);
              return (
                <li key={line.id}>
                  <button
                    type="button"
                    aria-pressed={done}
                    onClick={() =>
                      setChecked((current) => {
                        const next = new Set(current);
                        if (next.has(line.id)) next.delete(line.id);
                        else next.add(line.id);
                        return next;
                      })
                    }
                    className={`kw-tile flex min-h-14 w-full items-center gap-3 text-left text-lg ${
                      done ? 'border-good text-ink-muted line-through' : ''
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`flex size-7 shrink-0 items-center justify-center rounded-full border ${
                        done ? 'border-good bg-good text-surface' : 'border-line'
                      }`}
                    >
                      {done ? '✓' : ''}
                    </span>
                    <span className="min-w-0 break-words">{line.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <button type="button" onClick={() => setPhase(0)} className="kw-btn">
            {steps.length > 0 ? t('cook.start') : t('cook.finish')}
          </button>
        </div>
      ) : !atEnd ? (
        <div className="flex flex-1 flex-col gap-4">
          <p className="kw-kicker" aria-live="polite">
            {t('cook.stepOf', { number: phase + 1, total: steps.length })}
          </p>
          <p className="flex-1 text-2xl leading-snug break-words">{steps[phase]}</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setPhase(phase - 1)}
              disabled={phase === 0 && lines.length === 0}
              className="kw-btn kw-btn-quiet min-h-16"
            >
              {t('cook.previous')}
            </button>
            <button type="button" onClick={() => setPhase(phase + 1)} className="kw-btn min-h-16">
              {phase === steps.length - 1 ? t('cook.finish') : t('cook.next')}
            </button>
          </div>
        </div>
      ) : (
        <CookNoteForm recipeId={recipe.id} onDone={onClose} />
      )}
    </section>
  );
}

/** Wie war es? Daumen und ein Satz — beim nächsten Mal steht es oben auf der Seite. */
function CookNoteForm({ recipeId, onDone }: { recipeId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const noteId = useId();
  const [rating, setRating] = useState<1 | -1 | null>(null);
  const [note, setNote] = useState('');

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        void readProfile(db)
          .then((profile) =>
            addCookNote({ recipeId, cookedOn: today(profile.timezone), rating, note }, db),
          )
          .then(onDone);
      }}
    >
      <p className="font-display text-xl font-bold">{t('cook.howWasIt')}</p>
      <div role="group" aria-label={t('cook.rating')} className="grid grid-cols-2 gap-3">
        <button
          type="button"
          aria-pressed={rating === 1}
          onClick={() => setRating(rating === 1 ? null : 1)}
          className={`kw-btn kw-btn-quiet min-h-16 ${rating === 1 ? 'border-good text-good' : ''}`}
        >
          👍 {t('dish.ratingUp')}
        </button>
        <button
          type="button"
          aria-pressed={rating === -1}
          onClick={() => setRating(rating === -1 ? null : -1)}
          className={`kw-btn kw-btn-quiet min-h-16 ${rating === -1 ? 'border-warn text-warn' : ''}`}
        >
          👎 {t('dish.ratingDown')}
        </button>
      </div>
      <label htmlFor={noteId} className="flex flex-col gap-1">
        <span className="kw-label">{t('cook.note')}</span>
        <textarea
          id={noteId}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={3}
          maxLength={1000}
          placeholder={t('cook.noteHint')}
          className="kw-field"
        />
      </label>
      <button type="submit" className="kw-btn">
        {t('cook.saveNote')}
      </button>
      <button type="button" onClick={onDone} className="kw-btn kw-btn-quiet kw-btn-small">
        {t('cook.skipNote')}
      </button>
    </form>
  );
}
